/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useMemo } from 'react';

import { formatDateTime, presentUserOrgRole } from '@kaiizen/kai-presenters';
import { type ApiScanNote, getFlattenedScanNotes } from '@kaiizen/kai-pro-api';
import type { ScanPartOptimized } from '@kaiizen/kai-scan-template-system';

import { useNotesDrawerOptional, useShowNotes } from '@/Client/State';
import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { useUsers } from '@kaiizen/kai-pro-api-react-state';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  FOCUSED_MODULE_ACTION_STYLE,
  FOCUSED_MODULE_BOX_STYLE,
  FOCUSED_MODULE_COUNT_STYLE,
  FOCUSED_MODULE_EMPTY_PLAIN_STYLE,
  FOCUSED_MODULE_HEADER_ACTIONS_STYLE,
  FOCUSED_MODULE_HEADER_STYLE,
  FOCUSED_MODULE_TITLE_STYLE,
} from './focusedModuleStyles';

export const FOCUSED_NOTES_TITLE_TEXT = 'Notes';
export const FOCUSED_NOTES_UNKNOWN_USER_TEXT = 'Someone';

const NOTE_CARD_STYLE = css`
  label: FOCUSED_NOTES_CARD_STYLE;
  background: ${styleguideColors.sunken};
  border-left: 2px solid ${styleguideColors.stroke};
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NOTE_HEAD_STYLE = css`
  label: FOCUSED_NOTES_HEAD_STYLE;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const NOTE_WHO_STYLE = css`
  label: FOCUSED_NOTES_WHO_STYLE;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(11)};
  color: ${styleguideColors.textSecondary};

  b {
    font-weight: 700;
    color: ${styleguideColors.ink};
  }
`;

const NOTE_TIME_STYLE = css`
  label: FOCUSED_NOTES_TIME_STYLE;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10)};
  letter-spacing: 0.02em;
  color: ${styleguideColors.textTertiary};
  white-space: nowrap;
`;

const NOTE_BODY_STYLE = css`
  label: FOCUSED_NOTES_BODY_STYLE;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(14)};
  line-height: 1.45;
  color: ${styleguideColors.ink};
`;

/**
 * Every note on a scan part, newest first, with edits folded into the note they
 * edit and deleted notes resolved away. Notes with nothing written in them —
 * bare approvals and edit-requests carry a status and an empty body — are not
 * something there is anything to read, so they stay out of both the preview and
 * the count.
 */
const getVisibleNotes = (notes: ApiScanNote[]): ApiScanNote[] =>
  getFlattenedScanNotes([...notes])
    .filter(note => note.body?.trim())
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());

/**
 * The notes module: a peek at the most recent note, the full count beside the
 * title, and an add button. Every control opens the notes drawer: this module
 * reads, the drawer writes.
 *
 * One component serves all three editor scopes; only `emptyLabel` differs, since
 * "no notes yet" needs to name what has none.
 */
export const FocusedNotesSection = ({
  notes,
  scanPart,
  emptyLabel,
  notesLabel,
}: {
  notes: ApiScanNote[];
  scanPart?: ScanPartOptimized;
  emptyLabel: string;
  notesLabel?: string;
}) => {
  const { showNotesEditor } = useShowNotes();
  const notesDrawer = useNotesDrawerOptional();
  const { usersById } = useUsers();

  const visibleNotes = useMemo(() => getVisibleNotes(notes), [notes]);
  const latestNote = visibleNotes[0];
  const noteCount = visibleNotes.length;

  // The scope editor's notes drawer when the page has one — it follows the navigator, so it opens
  // on this same level without being told. Elsewhere, the older notes flyout for this one part.
  const openNotes = ({ focusComposer = false }: { focusComposer?: boolean } = {}) => {
    if (notesDrawer) notesDrawer.openNotesDrawer({ focusComposer });
    else if (scanPart) showNotesEditor({ scanPart });
  };

  const author = latestNote ? usersById?.[latestNote.userId] : undefined;
  const authorRole = author?.role ? presentUserOrgRole(author.role) : undefined;

  return (
    <div css={FOCUSED_MODULE_BOX_STYLE} data-testid={'focused-notes-section'}>
      <div css={FOCUSED_MODULE_HEADER_STYLE}>
        <h3 css={FOCUSED_MODULE_TITLE_STYLE}>
          {notesLabel ?? FOCUSED_NOTES_TITLE_TEXT}
          {noteCount > 0
            ? (
              <>
                {' '}
                <button
                  type={'button'}
                  css={FOCUSED_MODULE_COUNT_STYLE}
                  onClick={() => openNotes()}
                  title={'Open the notes editor'}
                  data-testid={'focused-notes-count'}
                >
                  {`• ${noteCount} total ${noteCount === 1 ? 'note' : 'notes'}`}
                </button>
              </>
            )
            : null}
        </h3>
        <div css={FOCUSED_MODULE_HEADER_ACTIONS_STYLE}>
          <button
            type={'button'}
            css={FOCUSED_MODULE_ACTION_STYLE}
            onClick={() => openNotes({ focusComposer: true })}
            title={latestNote ? 'Add a note' : 'Add the first note'}
            data-testid={'focused-notes-add'}
          >
            + Add
          </button>
        </div>
      </div>
      {latestNote
        ? (
          <div css={NOTE_CARD_STYLE} data-testid={'focused-notes-latest'}>
            <div css={NOTE_HEAD_STYLE}>
              <span css={NOTE_WHO_STYLE}>
                <b>{author?.name ?? FOCUSED_NOTES_UNKNOWN_USER_TEXT}</b>
                {authorRole ? ` · ${authorRole}` : ''}
              </span>
              <span css={NOTE_TIME_STYLE}>{formatDateTime(latestNote.createdAt)}</span>
            </div>
            <div css={NOTE_BODY_STYLE}>{latestNote.body.trim()}</div>
          </div>
        )
        : <div css={FOCUSED_MODULE_EMPTY_PLAIN_STYLE}>{emptyLabel}</div>}
    </div>
  );
};
